<template>
  <div class="explorer">
    <div class="cs-chips">
      <button
        v-for="c in charts"
        :key="c.key"
        class="cs-chip"
        :class="{ active: c.key === selectedChart }"
        @click="select(c.key)"
      >{{ c.label }}</button>
    </div>
    <div class="cs-stage" ref="stage"></div>
    <div class="cs-meta">
      <p class="cs-blurb">{{ current.blurb }}</p>
      <details class="cs-definition"><summary>View scene definition</summary><pre class="cs-code"><code>{{ current.schema }}</code></pre></details>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChartExplorer',
  data() {
    return {
      selectedChart: 'text',
      inView: false,
      builders: null,
      chartData: {},
      charts: [
        { key: 'text', label: 'Text', blurb: 'A text-led scene with inline emphasis and animated entry.',
          schema: "{\n  type: 'text',\n  figSel: '#intro',\n  props: {\n    title: 'Introduce one idea at a time',\n    bodyHTML: 'Inline ==highlight==, __underline__, **bold**.',\n    align: 'center',\n  },\n}" },
        { key: 'heatmap', label: 'Heatmap', blurb: 'Values shown across a two-dimensional grid.',
          schema: "{\n  type: 'heatmap',\n  figSel: '#load',\n  props: {\n    months: [1 … 12],\n    hours: [0 … 23],\n    getVal: (m, h) => load[m][h],\n  },\n}" },
        { key: 'choropleth', label: 'Choropleth', blurb: 'Regional values encoded through area shading.',
          schema: "{\n  type: 'geo',\n  figSel: '#map',\n  props: {\n    basemap: {\n      choropleth: {\n        valueByName: 'indexByState',\n        color: { range: ['#dedede', '#555555'] },\n      },\n    },\n  },\n}" },
        { key: 'bubblemap', label: 'Bubble map', blurb: 'Magnitudes shown as sized points on a map.',
          schema: "{\n  type: 'geo',\n  figSel: '#sites',\n  props: {\n    layers: {\n      bubbles: {\n        data: 'sites',\n        r: 'value',\n        rRange: [6, 30],\n      },\n    },\n  },\n}" },
        { key: 'scatter', label: 'Scatter', blurb: 'Two variables plotted with a fitted trend line.',
          schema: "{\n  type: 'scatter',\n  figSel: '#fit',\n  props: {\n    points: 'sites',\n    xLabel: 'Distance to grid',\n    yLabel: 'Capacity',\n  },\n}" },
        { key: 'line', label: 'Line', blurb: 'Time-series values revealed as the lines are drawn.',
          schema: "{\n  type: 'line',\n  figSel: '#capacity',\n  props: {\n    series: [\n      { id: 'Solar', data: 'solar' },\n      { id: 'Wind',  data: 'wind'  },\n    ],\n    axes: { xLabel: 'Year' },\n  },\n}" },
        { key: 'sankey', label: 'Sankey', blurb: 'Flow widths show the quantities carried along each connection.',
          schema: "{\n  type: 'sankey',\n  figSel: '#flows',\n  props: {\n    nodes: 'stations',\n    links: 'transfers',\n    valueLabel: ' TWh',\n  },\n}" },
      ],
    };
  },
  computed: {
    current() {
      return this.charts.find((c) => c.key === this.selectedChart) || this.charts[0];
    },
  },
  created() {
    this.chartData = this.buildChartData();
  },
  mounted() {
    this.$nextTick(async () => {
      const stage = this.$refs.stage;
      if (stage && 'IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting && !this.inView) {
              this.inView = true;
              this.renderCurrent();
            }
          });
        }, { threshold: 0.2 });
        this._io.observe(stage);
      } else {
        this.inView = true;
      }
      try {
        const m = await Promise.all([
          import('../../scrolly/text.js'),
          import('../../scrolly/heatmap.js'),
          import('../../scrolly/geo.js'),
          import('../../scrolly/scatter.js'),
          import('../../scrolly/line.js'),
          import('../../scrolly/sankey.js'),
        ]);
        this.builders = {
          text: m[0].build,
          heatmap: m[1].build,
          choropleth: m[2].build,
          bubblemap: m[2].build,
          scatter: m[3].build,
          line: m[4].build,
          sankey: m[5].build,
        };
        if (this.inView) this.renderCurrent();
      } catch (err) {
        console.error('scrollytelling figures failed to load:', err);
      }
    });
  },
  unmounted() {
    if (this._io) this._io.disconnect();
  },
  methods: {
    select(key) {
      this.selectedChart = key;
      this.$nextTick(() => this.renderCurrent());
    },
    renderCurrent() {
      const el = this.$refs.stage;
      if (!el || !this.builders) return;
      const build = this.builders[this.selectedChart];
      const props = this.chartData[this.selectedChart];
      if (!build || !props) return;
      el.innerHTML = '';
      el.classList.remove('fx-on');
      el.classList.toggle('is-text', this.selectedChart === 'text');
      build(el, props);
    },
    buildChartData() {
      const yrs = [2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024];
      const solar = yrs.map((y, i) => ({ x: y, y: Math.round(6 + i * i * 0.9 + i * 2) }));
      const wind = yrs.map((y, i) => ({ x: y, y: Math.round(22 + i * 5.2) }));

      const ridgeNames = ['2020', '2021', '2022', '2023', '2024'];
      const series = {};
      ridgeNames.forEach((name, i) => {
        const peak = 12 + i * 6;
        const sigma = 7;
        const arr = [];
        for (let x = 0; x <= 50; x += 2) arr.push({ x, y: Math.exp(-((x - peak) ** 2) / (2 * sigma * sigma)) });
        series[name] = arr;
      });

      const fuels = ['Coal', 'Gas', 'Solar', 'Wind', 'Hydro'];
      const matrix = [
        [0, 5, 8, 6, 4], [5, 0, 7, 9, 3], [8, 7, 0, 10, 6], [6, 9, 10, 0, 5], [4, 3, 6, 5, 0],
      ];

      const nodes = ['Coal', 'Gas', 'Solar', 'Wind', 'Grid', 'Homes', 'Industry', 'Data centers'].map((name) => ({ name }));
      const links = [
        { source: 'Coal', target: 'Grid', value: 24 }, { source: 'Gas', target: 'Grid', value: 30 },
        { source: 'Solar', target: 'Grid', value: 16 }, { source: 'Wind', target: 'Grid', value: 20 },
        { source: 'Grid', target: 'Homes', value: 34 }, { source: 'Grid', target: 'Industry', value: 28 },
        { source: 'Grid', target: 'Data centers', value: 28 },
      ];

      const points = [];
      for (let i = 0; i < 44; i++) {
        const dist = i * 2.2;
        const noise = 16 * Math.sin(i * 1.7) + 10 * Math.cos(i * 0.6);
        points.push({ dist: Math.round(dist), cap: Math.round(Math.max(2, 0.6 * dist + 14 + noise)), name: 'Site ' + (i + 1) });
      }

      const indexByState = {
        California: 0.95, Texas: 0.82, Virginia: 0.9, 'New York': 0.7, Washington: 0.62, Oregon: 0.55,
        Illinois: 0.5, Georgia: 0.6, Arizona: 0.66, Ohio: 0.45, Iowa: 0.5, Nevada: 0.52, Florida: 0.56,
        Nebraska: 0.4, 'North Carolina': 0.5, Utah: 0.48, Colorado: 0.46, Pennsylvania: 0.42,
      };

      const cities = [
        { name: 'New York', lon: -74.0, lat: 40.7, value: 90 }, { name: 'Los Angeles', lon: -118.2, lat: 34.0, value: 74 },
        { name: 'Chicago', lon: -87.6, lat: 41.9, value: 55 }, { name: 'Dallas', lon: -96.8, lat: 32.8, value: 60 },
        { name: 'Atlanta', lon: -84.4, lat: 33.7, value: 48 }, { name: 'Seattle', lon: -122.3, lat: 47.6, value: 40 },
        { name: 'Denver', lon: -105.0, lat: 39.7, value: 34 }, { name: 'Miami', lon: -80.2, lat: 25.8, value: 42 },
      ];

      return {
        text: {
          align: 'center',
          kicker: 'Text scene',
          title: 'Introduce one idea at a time',
          bodyHTML: 'A scene can ==highlight a finding==, __underline a key term__, or make a word **stand out** as it enters the page.',
        },
        heatmap: {
          colorRange: ['#eeeeee', '#666666'],
          months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
          hours: Array.from({ length: 24 }, (_, h) => h),
          xLabel: 'Month',
          yLabel: 'Hour of day',
          getVal: (m, h) => {
            const day = Math.exp(-((h - 15) ** 2) / (2 * 25));
            const summer = 0.5 + 0.5 * Math.sin(((m - 3) / 12) * 2 * Math.PI);
            return Math.round((0.25 + 0.75 * day) * (0.55 + 0.45 * summer) * 100);
          },
        },
        choropleth: {
          basemap: {
            stateFill: 'rgba(128,128,128,0.12)',
            stateStroke: 'rgba(128,128,128,0.40)',
            stateStrokeWidth: 0.6,
            choropleth: { valueByName: indexByState, color: { range: ['#dedede', '#555555'], domain: [0, 1] }, legend: { title: 'Index' } },
          },
          anim: { fadeMs: 600 },
        },
        bubblemap: {
          basemap: { stateFill: 'rgba(128,128,128,0.10)', stateStroke: 'rgba(128,128,128,0.35)', stateStrokeWidth: 0.6 },
          layers: {
            bubbles: {
              data: cities,
              lon: 'lon',
              lat: 'lat',
              r: 'value',
              rRange: [7, 30],
              style: { fill: 'rgba(128,128,128,0.25)', stroke: 'var(--chart-ink)', strokeWidth: 1.6 },
              label: { show: true, text: (d) => d.name, fontSize: '13px' },
              tooltip: (d) => `<strong>${d.name}</strong><br/>${d.value}`,
              anim: { growMs: 1200 },
            },
          },
        },
        scatter: { pointColor: 'var(--chart-muted)', lineColor: 'var(--chart-ink)', points, xLabel: 'Distance to grid', yLabel: 'Capacity', regRange: [0, 100] },
        line: {
          series: [
            { id: 'Solar', data: solar, styles: { stroke: 'var(--chart-ink)', strokeWidth: 3 }, marker: { show: true } },
            { id: 'Wind', data: wind, styles: { stroke: '#999999', strokeWidth: 3 }, marker: { show: true } },
          ],
          axes: { xLabel: 'Year' },
          anim: { drawMs: 1600 },
        },
        sankey: { colors: ['#555555', '#777777', '#999999', '#bbbbbb', '#777777', '#555555', '#999999', '#bbbbbb'], nodes, links, valueLabel: ' TWh' },
        ridge: { series, xDomain: [0, 50] },
        chord: { matrix, names: fuels },
      };
    },
  },
};
</script>

<style scoped>
.explorer {
  --chart-ink: #2f2f2f;
  --chart-muted: #767676;
  --chart-line: #e3e3e3;
  --chart-accent: #444444;
  margin: 1.3rem 0 0.5rem;
  border: 1px solid var(--chart-line);
  border-radius: 12px;
  padding: 16px;
  background: rgba(128, 128, 128, 0.045);
}
.cs-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
.cs-chip {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.82rem;
  color: var(--chart-muted);
  background: transparent;
  border: 1px solid var(--chart-line);
  border-radius: 999px;
  padding: 5px 14px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.cs-chip:hover { color: var(--chart-ink); border-color: var(--chart-accent); }
.cs-chip.active { color: #fff; background: var(--chart-accent); border-color: var(--chart-accent); }
.cs-stage {
  --gridline: rgba(0, 0, 0, 0.10);
  --danger: #d1495b;
  height: clamp(320px, 52vh, 480px);
  border: 1px solid var(--chart-line);
  border-radius: 10px;
  background: #fff;
  padding: 16px 18px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.cs-stage :deep(svg) { width: 100%; height: 100%; }
.cs-stage :deep(text) { fill: var(--chart-ink); }
.cs-stage :deep(.tick text) { fill: var(--chart-muted); font-size: 13px; }
.cs-stage :deep(.tick line),
.cs-stage :deep(.domain) { stroke: var(--gridline); }
.cs-stage :deep(.axis-title) { fill: var(--chart-muted); font-size: 14px; }
.cs-stage :deep(.text-kicker) { font-family: "Quattrocento Sans", sans-serif; font-size: 0.7rem; letter-spacing: 0.16em; text-transform: uppercase; color: var(--chart-muted); margin-bottom: 0.7rem; }
.cs-stage :deep(.text-title) { font-family: "PT Serif", Georgia, serif; font-weight: 700; color: var(--chart-ink); margin: 0 0 0.9rem; line-height: 1.18; font-size: clamp(1.6rem, 3.4vw, 2.3rem); }
.cs-stage :deep(.text-body) { color: var(--chart-muted); line-height: 1.62; max-width: 46ch; margin: 0 auto; font-size: clamp(0.98rem, 1.4vw, 1.12rem); }
.cs-stage :deep(.fx-bold) { font-weight: 700; color: var(--chart-ink); }
.cs-stage :deep(.fx-underline) { background-image: linear-gradient(currentColor, currentColor); background-position: 0 100%; background-repeat: no-repeat; background-size: 0% 2px; transition: background-size 0.55s ease; }
.cs-stage :deep(.fx-highlight) { background-image: linear-gradient(rgba(128, 128, 128, 0.28), rgba(128, 128, 128, 0.28)); background-position: 0 86%; background-repeat: no-repeat; background-size: 0% 0.92em; transition: background-size 0.55s ease; border-radius: 2px; }
.cs-stage.fx-on :deep(.fx-underline) { background-size: 100% 2px; }
.cs-stage.fx-on :deep(.fx-highlight) { background-size: 100% 0.92em; }
.cs-meta { margin-top: 14px; }
.cs-definition summary { cursor: pointer; color: inherit; font-size: .85rem; padding-block: .5rem; }
.cs-definition summary:focus-visible { outline: 2px solid currentColor; outline-offset: 3px; }
.cs-blurb { margin: 0 0 8px; color: var(--chart-muted); font-size: 0.92rem; }
.cs-code { margin: 0; background: #171717; border-radius: 8px; padding: 14px 16px; overflow-x: auto; }
.cs-code code { background: none; padding: 0; color: #e8e8e8; font-size: 0.82rem; line-height: 1.55; white-space: pre; }

:global(body.dark-mode .explorer) {
  --chart-ink: #ececec;
  --chart-muted: #b6b6b6;
  --chart-line: #393939;
  --chart-accent: #d7d7d7;
  background: rgba(128, 128, 128, 0.065);
}
:global(body.dark-mode .cs-chip) { border-color: #454545; color: #c1c1c1; }
:global(body.dark-mode .cs-chip:hover) { border-color: var(--chart-accent); color: #f5f5f5; }
:global(body.dark-mode .cs-chip.active) {
  border-color: var(--chart-accent);
  background: var(--chart-accent);
  color: #171717;
}
:global(body.dark-mode .cs-stage) {
  --gridline: rgba(255, 255, 255, 0.16);
  border-color: #3b3b3b;
  background: #181818;
}
:global(body.dark-mode .cs-stage .fx-highlight) {
  background-image: linear-gradient(rgba(128, 128, 128, 0.32), rgba(128, 128, 128, 0.32));
}

@media (max-width: 760px) {
  .cs-stage { height: clamp(300px, 64vw, 380px); }
}

@container (max-width: 760px) {
  .cs-stage { height: clamp(300px, 64cqw, 380px); }
}
</style>
